import dayjs from 'dayjs';
import {
  Truck,
  Calendar,
  Package,
  Percent,
  Building2,
  Clock
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type IncomeCellProps = {
  LastIncomeDate: string;
  supplierName: string;
  discount: number;
  Qty: number;
};

function LastIncomeColumn({
  LastIncomeDate,
  Qty,
  discount,
  supplierName,
}: IncomeCellProps) {
  const now = dayjs();
  const incomingDate = dayjs(LastIncomeDate);
  const daysDiff = now.diff(incomingDate, 'day');
  
  // Determine urgency color based on days difference
  const getUrgencyStyles = () => {
    if (!LastIncomeDate) return { gradient: 'from-gray-500 to-gray-600', pulse: false };
    if (daysDiff <= 7) return { gradient: 'from-green-500 to-green-600', pulse: false };
    if (daysDiff <= 30) return { gradient: 'from-blue-500 to-blue-600', pulse: false };
    if (daysDiff <= 60) return { gradient: 'from-amber-500 to-amber-600', pulse: false };
    return { gradient: 'from-red-500 to-red-600', pulse: true };
  };
  
  const { gradient, pulse } = getUrgencyStyles();
  
  return (
    <div className="flex items-center justify-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            className={cn(
              "h-auto p-0 hover:bg-transparent",
              pulse && "animate-pulse"
            )}
          >
            <Badge className={cn(
              "flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold cursor-pointer",
              "bg-gradient-to-r text-white shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105",
              gradient,
              "border-0"
            )}>
              <Truck className="h-3.5 w-3.5" />
              <span className="font-medium">
                {LastIncomeDate ? incomingDate.from(now) : 'لا يوجد'}
              </span>
            </Badge>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          side="top" 
          className={cn(
            "w-[360px] p-0 overflow-hidden",
            "bg-gradient-to-br from-background via-background to-muted/10",
            "dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/30",
            "border-border/50 dark:border-slate-700/50 shadow-2xl"
          )}
        >
          <div className="bg-gradient-to-r from-indigo-50/50 to-blue-50/50 dark:from-indigo-950/30 dark:to-blue-950/30 p-3 border-b border-border/30">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-gradient-to-br from-indigo-600 to-blue-600 text-white">
                <Truck className="h-4 w-4" />
              </div>
              <span className="font-semibold text-sm">تفاصيل آخر توريد</span>
            </div>
          </div>
          
          <div className="p-4 space-y-3">
            {/* Supplier */}
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                <Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">المورد</p>
                <p className="text-sm font-semibold">{supplierName || '-'}</p>
              </div>
            </div>
            
            {/* Date */}
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-md bg-green-100 dark:bg-green-900/30">
                <Calendar className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">التاريخ</p>
                <p className="text-sm font-semibold">
                  {LastIncomeDate ? dayjs(LastIncomeDate).format('DD/MM/YYYY') : '-'}
                </p>
                {LastIncomeDate && (
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      منذ {daysDiff} يوم
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Quantity & Discount */}
            <div className="flex gap-3">
              <div className="flex-1 p-2 rounded-lg bg-gradient-to-r from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20 border border-violet-200 dark:border-violet-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                    <span className="text-xs text-muted-foreground">الكمية</span>
                  </div>
                  <span className="text-sm font-bold text-violet-700 dark:text-violet-300">
                    {formatNumberWithCommas(Qty)}
                  </span>
                </div>
              </div>
              
              <div className="flex-1 p-2 rounded-lg bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Percent className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs text-muted-foreground">الخصم</span>
                  </div>
                  <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                    {formatNumberWithCommas(discount)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default LastIncomeColumn;
